import React, { useState, useEffect } from "react";
import {
  Container,
  Spinner,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
  Table,
  UncontrolledTooltip,
} from "reactstrap";
import Select from "react-select";
import { Notification } from "../common/style";
import {
  getProductDefinitions,
  getQualifiedProperties,
  updateProductDefinition,
  generateProductDefinitions,
} from "../product/service";

const CatalogDefinitionManager = () => {
  const [definitions, setDefinitions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" or "edit"
  const [editingDefinition, setEditingDefinition] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    item_id: "",
    name: "",
    is_product: false,
    external_structure: "",
    listing_category: "",
    listing_sub_category: "",
    properties: [],
  });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setIsNotificationVisible(true);
  };

  const fetchProperties = async () => {
    try {
      const response = await getQualifiedProperties();
      setProperties(response || []);
    } catch (error) {
      console.error("Error loading properties:", error);
      showNotification(
        "error",
        error?.error || "Failed to load properties. Please try again."
      );
    }
  };

  const fetchProductDefinitions = async () => {
    try {
      setLoading(true);
      const response = await getProductDefinitions();
      setDefinitions(response || []);
    } catch (error) {
      console.error("Error loading product definitions:", error);
      showNotification(
        "error",
        error?.error || "Failed to load product definitions. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);

    if (isModalOpen) {
      setEditingDefinition(null);
      setModalMode("create");
      setFormData({
        item_id: "",
        name: "",
        is_product: false,
        external_structure: "",
        listing_category: "",
        listing_sub_category: "",
        properties: [],
      });
    }
  };

  const getDefaultOptionsForProperty = (property) => {
    const availableOptions = property.options || [];

    // If default_options is null or undefined, select all options
    if (
      property.default_options === null ||
      property.default_options === undefined
    )
      return availableOptions.map((opt) => opt.identifier);
    else
      return availableOptions
        .filter((opt) => property.default_options.includes(opt.identifier))
        .map((opt) => opt.identifier);
  };

  const openCreateModal = () => {
    // Find properties with default: true and auto-add them with default options
    const defaultProperties = properties
      .filter((prop) => prop.default === true)
      .map((property) => ({
        identifier: property.identifier,
        name: property.name,
        options: getDefaultOptionsForProperty(property),
      }));

    setFormData({
      item_id: "",
      name: "",
      is_product: false,
      external_structure: "",
      listing_category: "",
      listing_sub_category: "",
      properties: defaultProperties,
    });
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleEdit = (definition) => {
    setEditingDefinition(definition);
    // Convert definition.properties (object) to formData.properties (array)
    const propertiesArray = Object.entries(definition.properties || {}).map(
      ([identifier, options]) => {
        const property = properties.find((p) => p.identifier === identifier);

        return {
          identifier,
          name: property.name,
          options: options,
        };
      }
    );

    setFormData({
      item_id: definition.item_id,
      name: definition.name,
      is_product: definition.is_product || false,
      external_structure: definition.external_structure || "",
      listing_category: definition.listing_category || "",
      listing_sub_category: definition.listing_sub_category || "",
      properties: propertiesArray,
    });
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddProperty = (selectedProperty) => {
    if (selectedProperty) {
      const property = properties.find(
        (p) => p.identifier === selectedProperty.value
      );
      // Check if property already exists
      const exists = formData.properties.some(
        (p) => p.identifier === selectedProperty.value
      );
      if (!exists) {
        // Get default options for the property
        const defaultOptions = getDefaultOptionsForProperty(property);

        setFormData((prev) => {
          return {
            ...prev,
            properties: [
              ...prev.properties,
              {
                identifier: selectedProperty.value,
                name: property.name || selectedProperty.label,
                options: defaultOptions,
              },
            ],
          };
        });
      }
    }
  };

  const handleRemoveProperty = (index) => {
    setFormData((prev) => ({
      ...prev,
      properties: prev.properties.filter((_, i) => i !== index),
    }));
  };

  const handleOptionToggle = (propertyIndex, optionIdentifier) => {
    setFormData((prev) => {
      const newProperties = [...prev.properties];
      const currentOptions = newProperties[propertyIndex].options || [];
      const isSelected = currentOptions.includes(optionIdentifier);

      newProperties[propertyIndex] = {
        ...newProperties[propertyIndex],
        options: isSelected
          ? currentOptions.filter((opt) => opt !== optionIdentifier)
          : [...currentOptions, optionIdentifier],
      };
      return {
        ...prev,
        properties: newProperties,
      };
    });
  };

  const handleCheckAllOptions = (propertyIndex) => {
    setFormData((prev) => {
      const newProperties = [...prev.properties];
      const property = newProperties[propertyIndex];
      const propertyData = properties.find(
        (p) => p.identifier === property.identifier
      );
      const allOptionIdentifiers =
        propertyData?.options?.map((opt) => opt.identifier) || [];
      const currentOptions = property.options || [];
      const allSelected =
        allOptionIdentifiers.length > 0 &&
        allOptionIdentifiers.every((id) => currentOptions.includes(id));

      newProperties[propertyIndex] = {
        ...property,
        options: allSelected ? [] : allOptionIdentifiers,
      };
      return {
        ...prev,
        properties: newProperties,
      };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Convert properties array to object format: {hinge: ["ebco_normal_close"]}
      const propertiesObject = formData.properties.reduce((acc, prop) => {
        acc[prop.identifier] = prop.options;
        return acc;
      }, {});

      if (modalMode === "edit") {
        // Update mode
        const definitionId = editingDefinition.id;
        const payload = {
          item_id: editingDefinition.item_id || editingDefinition.id || "",
          name: formData.name,
          is_product: editingDefinition.is_product || false,
          external_structure: formData.external_structure,
          listing_category: formData.listing_category,
          listing_sub_category: formData.listing_sub_category,
          properties: propertiesObject,
        };
        const response = await updateProductDefinition(definitionId, payload);
        const updatedDefinition = response.definition;

        setDefinitions((prev) =>
          prev.map((def) =>
            def.id === updatedDefinition.id ? updatedDefinition : def
          )
        );

        showNotification("success", "Product definition updated successfully!");
      } else {
        // Create mode
        const payload = {
          item_id: formData.item_id,
          name: formData.name,
          is_product: formData.is_product,
          external_structure: formData.external_structure,
          listing_category: formData.listing_category,
          listing_sub_category: formData.listing_sub_category,
          properties: propertiesObject,
        };
        const response = await generateProductDefinitions(payload);
        const newDefinition = response.definition;

        setDefinitions((prev) => [...prev, newDefinition]);
        showNotification("success", "Products generated successfully!");
      }
      toggleModal();
    } catch (error) {
      console.error(
        `Error ${
          modalMode === "edit" ? "updating" : "generating"
        } product definition:`,
        error
      );
      showNotification(
        "error",
        error?.error ||
          `Failed to ${
            modalMode === "edit" ? "update" : "generate"
          } product definition. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    // Check if item_id is filled (for create mode)
    if (modalMode === "create")
      if (!formData.item_id || formData.item_id.trim() === "") return false;

    // Check if name is filled (mandatory)
    if (!formData.name || formData.name.trim() === "") return false;

    // Check if external_structure is filled (mandatory)
    if (
      !formData.external_structure ||
      formData.external_structure.trim() === ""
    )
      return false;

    // Check if listing_category is filled (mandatory)
    if (!formData.listing_category || formData.listing_category.trim() === "")
      return false;

    // Check if listing_sub_category is filled (mandatory)
    if (
      !formData.listing_sub_category ||
      formData.listing_sub_category.trim() === ""
    )
      return false;

    // Check if at least one property is added
    if (formData.properties.length === 0) return false;

    // Check if all properties have at least one option selected
    const allPropertiesHaveOptions = formData.properties.every(
      (property) => property.options && property.options.length > 0
    );

    return allPropertiesHaveOptions;
  };

  const getPropertyOptionNames = (propIdentifier, optionIdentifiers) => {
    const property = properties.find((p) => p.identifier === propIdentifier);
    if (!property || !property.options) return [];

    return optionIdentifiers.map((identifier) => {
      const option = property.options.find(
        (opt) => opt.identifier === identifier
      );
      return option?.name || identifier;
    });
  };

  const getGroupedProperties = (formProperties) => {
    const availableProperties = properties.filter(
      (prop) => !formProperties.some((p) => p.identifier === prop.identifier)
    );

    // Group by type
    const groupedByType = availableProperties.reduce((acc, prop) => {
      const type = prop.type || "Other";
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(prop);
      return acc;
    }, {});

    // Define the desired order
    const typeOrder = ["design", "build", "internal"];

    // Get all types and sort them according to the desired order
    const allTypes = Object.keys(groupedByType);
    const sortedTypes = [
      ...typeOrder.filter((type) => allTypes.includes(type)),
      ...allTypes.filter((type) => !typeOrder.includes(type)).sort(),
    ];

    // Convert to react-select grouped format
    return sortedTypes.map((type) => ({
      label: type,
      options: groupedByType[type].map((prop) => ({
        label: prop.name,
        value: prop.identifier,
      })),
    }));
  };

  const formatGroupLabel = (data) => (
    <>
      <div
        style={{
          padding: "0px",
        }}
      >
        {data.label}
      </div>
    </>
  );

  const filteredDefinitions = definitions.filter((definition) => {
    if (!searchTerm.trim()) return true;
    const name = definition.name || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    fetchProperties();
    fetchProductDefinitions();
  }, []);

  return (
    <Container className="p-4">
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={() => setIsNotificationVisible(false)}
        >
          {notification.message}
        </Notification>
      )}

      <div className="mb-4 d-flex justify-content-between align-items-center">
        <h1 className="m-0">Catalog Definition Manager</h1>
        <Button color="primary" onClick={openCreateModal}>
          Generate Definitions
        </Button>
      </div>

      <Row className="mb-3">
        <Col className="p-0">
          <Input
            type="text"
            className="rounded-1"
            placeholder="Search definitions by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <div className="mt-4">
          {definitions.length === 0 ? (
            <div className="text-center my-5">
              <p>No product definitions found</p>
            </div>
          ) : filteredDefinitions.length === 0 ? (
            <div className="text-center my-5">
              <p>No definitions match your search</p>
            </div>
          ) : (
            <Table striped bordered responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Properties</th>
                  <th>External Structure</th>
                  <th>Listing Category</th>
                  <th>Listing Sub Category</th>
                  <th className="text-center">Count</th>
                  <th className="text-center">Edit</th>
                </tr>
              </thead>
              <tbody>
                {filteredDefinitions.map((definition, index) => {
                  const propertyEntries = Object.entries(
                    definition.properties || {}
                  );

                  return (
                    <tr key={index}>
                      <td>
                        <span
                          className="link pointer"
                          onClick={() => {
                            const url = `/manage/catalog?definition=${encodeURIComponent(
                              definition.name
                            )}`;
                            window.open(url, "_blank");
                          }}
                        >
                          {definition.name}
                        </span>
                      </td>
                      <td>
                        {propertyEntries.length > 0 ? (
                          <div className="d-flex flex-wrap gap-2">
                            {propertyEntries.map(
                              (
                                [propIdentifier, optionIdentifiers],
                                propIndex
                              ) => {
                                const property = properties.find(
                                  (p) => p.identifier === propIdentifier
                                );
                                const propertyName =
                                  property?.name || propIdentifier;
                                const optionNames = getPropertyOptionNames(
                                  propIdentifier,
                                  Array.isArray(optionIdentifiers)
                                    ? optionIdentifiers
                                    : []
                                );
                                const tooltipId = `tooltip-${index}-${propIndex}`;

                                return (
                                  <span key={propIndex}>
                                    <span id={tooltipId}>{propertyName}</span>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={tooltipId}
                                    >
                                      {optionNames.length > 0
                                        ? optionNames.join(", ")
                                        : "No options"}
                                    </UncontrolledTooltip>
                                    {propIndex < propertyEntries.length - 1 &&
                                      ", "}
                                  </span>
                                );
                              }
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">No properties</span>
                        )}
                      </td>
                      <td>{definition.external_structure}</td>
                      <td>{definition.listing_category}</td>
                      <td>{definition.listing_sub_category}</td>
                      <td className="text-center">
                        {definition.products_count}
                      </td>
                      <td className="text-center">
                        <i
                          className="fa fa-edit link pointer"
                          onClick={() => handleEdit(definition)}
                        ></i>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      )}

      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          {modalMode === "edit" ? "Edit Product Definition" : "Create New Item"}
        </ModalHeader>
        <ModalBody>
          {modalMode === "create" && (
            <FormGroup>
              <Label>Item Id</Label>
              <Input
                type="text"
                name="item_id"
                value={formData.item_id}
                onChange={handleInputChange}
                placeholder="Enter Item ID"
                autoComplete="off"
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label>Name</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter Name"
              autoComplete="off"
            />
          </FormGroup>

          <FormGroup>
            <Label>External Structure</Label>
            <Input
              type="text"
              name="external_structure"
              value={formData.external_structure}
              onChange={handleInputChange}
              placeholder="Enter External Structure"
              autoComplete="off"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Listing Category</Label>
            <Input
              type="text"
              name="listing_category"
              value={formData.listing_category}
              onChange={handleInputChange}
              placeholder="Enter Listing Category"
              autoComplete="off"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Listing Sub Category</Label>
            <Input
              type="text"
              name="listing_sub_category"
              value={formData.listing_sub_category}
              onChange={handleInputChange}
              placeholder="Enter Listing Sub Category"
              autoComplete="off"
              required
            />
          </FormGroup>

          {modalMode === "create" && (
            <FormGroup>
              <Label check>
                <Input
                  className="mt-0 me-2"
                  type="checkbox"
                  name="is_product"
                  checked={formData.is_product}
                  onChange={handleInputChange}
                />
                Product
              </Label>
            </FormGroup>
          )}

          <div className="mb-3">
            <Select
              placeholder="Add Property"
              options={getGroupedProperties(formData.properties)}
              formatGroupLabel={formatGroupLabel}
              onChange={handleAddProperty}
              value={null}
            />
          </div>

          {formData.properties.map((property, index) => (
            <Card key={index} className="mb-3">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">{property.name}</h6>
                  <Button
                    color="link"
                    size="sm"
                    onClick={() => handleRemoveProperty(index)}
                  >
                    <i className="fa fa-times"></i>
                  </Button>
                </div>

                {property.identifier &&
                  properties.find((p) => p.identifier === property.identifier)
                    ?.options && (
                    <FormGroup>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Label>Options</Label>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="checkbox"
                              checked={
                                properties.find(
                                  (p) => p.identifier === property.identifier
                                )?.options?.length > 0 &&
                                properties
                                  .find(
                                    (p) => p.identifier === property.identifier
                                  )
                                  ?.options?.every((opt) =>
                                    property.options?.includes(opt.identifier)
                                  )
                              }
                              onChange={() => handleCheckAllOptions(index)}
                            />
                            Check All
                          </Label>
                        </FormGroup>
                      </div>
                      <div className="d-flex flex-wrap gap-3">
                        {properties
                          .find((p) => p.identifier === property.identifier)
                          ?.options?.map((option) => (
                            <FormGroup
                              key={option.identifier}
                              check
                              className="mt-0 ms-2"
                            >
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={property.options?.includes(
                                    option.identifier
                                  )}
                                  onChange={() =>
                                    handleOptionToggle(index, option.identifier)
                                  }
                                />
                                {option.name}
                              </Label>
                            </FormGroup>
                          ))}
                      </div>
                    </FormGroup>
                  )}
              </CardBody>
            </Card>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={!isFormValid() || submitting}
          >
            {submitting
              ? modalMode === "edit"
                ? "Updating..."
                : "Generating..."
              : modalMode === "edit"
              ? "Update"
              : "Generate"}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default CatalogDefinitionManager;
